/**
 * Login Modal Component
 *
 * A reusable modal for inline authentication without page redirects.
 * Supports email/password, passkey, and 2FA authentication.
 * Designed to be used on public pages that require login for actions.
 *
 * @author Andres Nava
 * @version 1.0.0
 */
import React, { useState, useEffect, useRef, useMemo, lazy, Suspense } from 'react';
// import { logger } from "../../utils/logger";
import { Box, Dialog, DialogTitle, DialogContent, DialogActions, Typography, TextField, Button, Alert, ToggleButton, ToggleButtonGroup, InputAdornment, IconButton, Link as MuiLink, CircularProgress, useMediaQuery, useTheme, Divider, } from '@mui/material';
import { Email, Lock, Visibility, VisibilityOff, Fingerprint, CheckCircle, Close, } from '@mui/icons-material';
import { useAuth } from '../../contexts/auth/authContext';
import { enhanceLoginForm, enhanceTOTPInput } from '../../utils/passwordManagerHelper';
import { authFetch } from '../../contexts/auth/authFetch';
import { csrfFetch } from '../../utils/csrfFetch';
import { getAuthUrl, resolveApiUrl } from '../../utils/subdomainUtils';
import { logger } from "../../utils/logger";

const PasskeyLogin = lazy(() => import('./PasskeyLogin'));

interface LoginModalProps {
    open: boolean;
    onClose: () => void;
    onSuccess: (user: any) => void;
    title?: string;
    description?: string;
}
const LoginModal: React.FC<LoginModalProps> = ({ open, onClose, onSuccess, title = 'Login Required', description = 'Please log in to continue' }) => {
    const { updateUserAndSession } = useAuth();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [formData, setFormData] = useState({ email: '', password: '', verificationCode: '' });
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [loginMethod, setLoginMethod] = useState<'passkey' | 'password'>('password');
    const [showPassword, setShowPassword] = useState(false);
    const [loginStep, setLoginStep] = useState<'credentials' | '2fa' | 'success'>('credentials');
    const [showSuccess, setShowSuccess] = useState(false);
    const totpInputRef = useRef<HTMLInputElement>(null);
    
    // Memoize auth URLs to prevent excessive getAuthUrl calls during re-renders
    const forgotPasswordUrl = useMemo(() => getAuthUrl('/forgot-password', { includeRedirect: false }), []);
    const signupUrl = useMemo(() => getAuthUrl('/signup', { includeRedirect: false }), []);
    // Reset state when modal closes
    useEffect(() => {
        if (!open) {
            setFormData({ email: '', password: '', verificationCode: '' });
            setError('');
            setLoading(false);
            setLoginStep('credentials');
            setShowSuccess(false);
            setLoginMethod('password');
            setShowPassword(false);
        }
    }, [open]);
    // Auto-focus and enhance form for password managers
    // This must happen on initial load so password managers can detect all fields including TOTP
    useEffect(() => {
        if (open && loginMethod === 'password') {
            setTimeout(() => {
                const form = document.getElementById('login-modal-form') as HTMLFormElement;
                if (form) {
                    enhanceLoginForm(form);
                    // Also enhance TOTP field immediately (it's in DOM but hidden)
                    // Don't focus it - only enhance attributes so password managers can detect it
                    const totpInput = form.querySelector('input[name="verificationCode"], input[autocomplete="one-time-code"]') as HTMLInputElement;
                    if (totpInput) {
                        enhanceTOTPInput(totpInput, false); // false = don't focus, just enhance attributes
                    }
                }
                // Focus email field if in credentials step
                if (loginStep === 'credentials') {
                    document.getElementById('modal-email')?.focus();
                }
            }, 100);
        }
    }, [open, loginMethod]);
    // Re-enhance form when TOTP field becomes visible to ensure password manager detection
    useEffect(() => {
        if (loginStep === '2fa' && open) {
            // Use multiple timeouts to ensure Dialog is fully rendered and visible
            const timeout1 = setTimeout(() => {
                const form = document.getElementById('login-modal-form') as HTMLFormElement;
                // Try ref first, then fallback to DOM query
                const twoFAInput = totpInputRef.current ||
                    document.getElementById('modal-2fa-code') as HTMLInputElement ||
                    document.querySelector('input[name="totp"]') as HTMLInputElement;
                if (form && twoFAInput) {
                    // Re-enhance the form to include the new 2FA field
                    enhanceLoginForm(form);
                    // Also specifically enhance the 2FA input
                    enhanceTOTPInput(twoFAInput, true);
                }
            }, 100);
            // Second timeout for additional enhancement after Dialog animation
            const timeout2 = setTimeout(() => {
                const form = document.getElementById('login-modal-form') as HTMLFormElement;
                const twoFAInput = totpInputRef.current ||
                    document.getElementById('modal-2fa-code') as HTMLInputElement ||
                    document.querySelector('input[name="totp"]') as HTMLInputElement;
                if (form && twoFAInput) {
                    // Ensure field is visible and trigger events
                    enhanceTOTPInput(twoFAInput, true);
                    // Trigger multiple events to notify password managers
                    requestAnimationFrame(() => {
                        twoFAInput.focus();
                        twoFAInput.click();
                        twoFAInput.dispatchEvent(new Event('focus', { bubbles: true, cancelable: true }));
                        twoFAInput.dispatchEvent(new Event('click', { bubbles: true, cancelable: true }));
                        twoFAInput.dispatchEvent(new Event('input', { bubbles: true, cancelable: true }));
                        twoFAInput.dispatchEvent(new Event('change', { bubbles: true, cancelable: true }));
                        form.dispatchEvent(new Event('input', { bubbles: true, cancelable: true }));
                        form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: false }));
                    });
                }
            }, 400);
            // Third timeout for password managers that scan later
            const timeout3 = setTimeout(() => {
                const form = document.getElementById('login-modal-form') as HTMLFormElement;
                const twoFAInput = totpInputRef.current ||
                    document.getElementById('modal-2fa-code') as HTMLInputElement ||
                    document.querySelector('input[name="totp"]') as HTMLInputElement;
                if (form && twoFAInput) {
                    // Final trigger for slower password managers
                    twoFAInput.dispatchEvent(new Event('focus', { bubbles: true }));
                    form.dispatchEvent(new Event('input', { bubbles: true }));
                }
            }, 800);
            return () => {
                clearTimeout(timeout1);
                clearTimeout(timeout2);
                clearTimeout(timeout3);
            };
        }
    }, [loginStep, open]);
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const fieldName = e.target.name === 'totp' ? 'verificationCode' : e.target.name;
        setFormData(prev => ({ ...prev, [fieldName]: e.target.value }));
        setError('');
    };
    const validateForm = (): boolean => {
        // Email validation
        if (!formData.email || !/\S+@\S+\.\S+/.test(formData.email)) {
            setError('Please enter a valid email address');
            return false;
        }
        // Password required for password login
        if (loginMethod === 'password' && loginStep === 'credentials' && !formData.password) {
            setError('Password is required');
            return false;
        }
        // 2FA code required when in 2FA step
        if (loginStep === '2fa' && !formData.verificationCode.trim()) {
            setError('Please enter the verification code from your authenticator app');
            return false;
        }
        return true;
    };
    const handleRetry = () => {
        setError('');
        setLoading(false);
    };
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (!validateForm()) {
            return;
        }
        setLoading(true);
        setError('');
        try {
            // Step 1: Check if 2FA is required (if we're in credentials step)
            if (loginStep === 'credentials') {
                // authFetch attaches CSRF when a session cookie exists (sid is HttpOnly).
                const check2FAResponse = await authFetch('/api/auth/check-2fa', {
                    method: 'POST',
                    body: JSON.stringify({ email: formData.email, password: formData.password }),
                });
                // needs2FASetup means enroll TOTP after login — do not ask for an authenticator
                // code (dev often has coordinator/admin roles without copied mfa_methods).
                if (check2FAResponse.success && check2FAResponse.data?.requires2FA) {
                    setLoginStep('2fa');
                    setError('');
                    setLoading(false);
                    setTimeout(() => {
                        document.getElementById('modal-2fa-code')?.focus();
                    }, 100);
                    return;
                }
            }
            // Step 2: Perform login
            const loginData: any = {
                email: formData.email,
                password: formData.password
            };
            if (loginStep === '2fa' && formData.verificationCode.trim()) {
                loginData.verificationCode = formData.verificationCode.trim();
            }
            // csrfFetch always attaches (and retries once on 403). Do not use raw fetch with
            // an optional prefetched token — that races and omits the header when sid exists
            // (ST-2026-219 / ST-2026-220: CSRF token missing on POST /api/auth/login).
            const postLogin = async (payload: Record<string, string>) => {
                const response = await csrfFetch(resolveApiUrl('/api/auth/login'), {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload),
                });
                const data = await response.json().catch(() => ({}));
                return { response, data };
            };
            let { response, data } = await postLogin(loginData);
            // Dev/prod copy: role requires MFA setup but no enabled TOTP row yet.
            if (response.status === 400 && data.error === '2FA_NOT_ENABLED' && loginData.verificationCode) {
                ({ response, data } = await postLogin({
                    email: String(loginData.email),
                    password: String(loginData.password),
                }));
            }
            // Handle rate limiting
            if (response.status === 429) {
                const retryAfter = typeof data.retryAfter === 'number' ? data.retryAfter : null;
                const retryHint =
                    retryAfter != null && retryAfter > 0
                        ? ` Please try again in ${Math.ceil(retryAfter / 60)} minute(s).`
                        : ' Please wait a few minutes and try again.';
                setError(`Too many login attempts.${retryHint}`);
                setLoading(false);
                return;
            }
            // Handle account status errors
            if (data.error_code === 'email_not_verified') {
                setError('Please verify your email address before logging in.');
                setLoading(false);
                return;
            }
            if (data.error_code === 'account_suspended') {
                setError('Your account has been suspended. Please contact support for assistance.');
                setLoading(false);
                return;
            }
            if (data.error_code === 'account_inactive') {
                setError('Your account is inactive. Please contact support to reactivate your account.');
                setLoading(false);
                return;
            }
            if (!response.ok || !data.success) {
                // Login can return 200 + success:false when MFA is required (check-2fa may have been skipped).
                if (data.requires2FA === true || data.error === '2FA_REQUIRED') {
                    setLoginStep('2fa');
                    setError('Enter the code from your authenticator app.');
                    setLoading(false);
                    setTimeout(() => {
                        document.getElementById('modal-2fa-code')?.focus();
                    }, 100);
                    return;
                }
                if (data.error === '2FA_NOT_ENABLED') {
                    setLoginStep('credentials');
                    setFormData((prev) => ({ ...prev, verificationCode: '' }));
                    setError('Two-factor is not enrolled for this account on this environment. Sign in without a code, then set up authenticator.');
                } else if (loginStep === '2fa') {
                    setError(data.message || data.error || 'Invalid verification code. Please try again.');
                }
                else {
                    setError(data.error || 'Invalid email or password');
                }
                setLoading(false);
                return;
            }
            // Success!
            const user = data.user || data.data?.user;
            if (user) {
                // Update global auth context
                updateUserAndSession(user, 'session-from-modal');
                // Show brief success animation
                setShowSuccess(true);
                setTimeout(() => {
                    setLoading(false);
                    onSuccess(user);
                }, 800);
            }
            else {
                setError('Login successful but user data not received. Please try again.');
                setLoading(false);
            }
        }
        catch (err) {
            logger.auth('Login error:', { "arg0": err });
            // Handle network errors specifically
            if (err instanceof Error && (err.message.includes('network') || err.message.includes('fetch'))) {
                setError('Network error. Please check your connection and try again.');
            }
            else {
                setError('Login failed. Please try again.');
            }
            setLoading(false);
        }
    };
    const handlePasskeySuccess = (user: any, sessionId: string) => {
        // Update global auth context
        updateUserAndSession(user, 'session-from-modal');
        // Show brief success animation
        setShowSuccess(true);
        setTimeout(() => {
            setLoading(false);
            onSuccess(user);
        }, 800);
    };
    const handlePasskeyFallback = () => {
        setLoginMethod('password');
    };
    const handleMethodChange = (_event: React.MouseEvent<HTMLElement>, newMethod: 'passkey' | 'password' | null) => {
        if (newMethod !== null) {
            setLoginMethod(newMethod);
            setError('');
            setLoginStep('credentials');
        }
    };
    // Prevent closing during active login
    const handleClose = () => {
        if (!loading) {
            onClose();
        }
    };
    return (<Dialog open={open} onClose={handleClose} disableEscapeKeyDown={loading} fullScreen={isMobile} maxWidth="sm" fullWidth PaperProps={{
            sx: {
                borderRadius: isMobile ? 0 : 2,
            }
        }}>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h5" component="div" sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
          <IconButton onClick={handleClose} disabled={loading} size="small" sx={{ color: 'text.secondary' }}>
            <Close />
          </IconButton>
        </Box>
        {description && (<Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {description}
          </Typography>)}
      </DialogTitle>

      <DialogContent>
        {/* Success animation */}
        {showSuccess && (<Box sx={{ textAlign: 'center', py: 4 }}>
            <CheckCircle sx={{ fontSize: 80, color: 'success.main', mb: 2 }}/>
            <Typography variant="h6" color="success.main">
              Login Successful!
            </Typography>
          </Box>)}

        {/* Login form */}
        {!showSuccess && (<Box sx={{ mt: 2 }}>
            {/* Method toggle */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
              <ToggleButtonGroup value={loginMethod} exclusive onChange={handleMethodChange} size="small" disabled={loading}>
                <ToggleButton value="password">
                  <Lock sx={{ mr: 1, fontSize: '1.2rem' }}/>
                  Password
                </ToggleButton>
                <ToggleButton value="passkey">
                  <Fingerprint sx={{ mr: 1, fontSize: '1.2rem' }}/>
                  Passkey
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {/* Error message */}
            {error && (<Alert severity="error" sx={{ mb: 2 }} action={error.includes('Network') ? (<Button size="small" onClick={handleRetry}>
                      Retry
                    </Button>) : undefined}>
                {error}
              </Alert>)}

            {/* Password login form */}
            {loginMethod === 'password' && (<Box component="form" id="login-modal-form" onSubmit={handleSubmit} action="/login" method="post" autoComplete="on">
                {/* Email field */}
                <TextField id="modal-email" name="email" label="Email" type="email" fullWidth autoComplete="email username" value={formData.email} onChange={handleChange} disabled={loading || loginStep === '2fa'} InputProps={{
                    startAdornment: (<InputAdornment position="start">
                        <Email />
                      </InputAdornment>),
                }} sx={{
                    mb: 2,
                    display: loginStep === 'credentials' ? 'block' : 'none', // Hide when in 2FA step
                }}/>

                {/* Password field */}
                <TextField id="modal-password" name="password" label="Password" type={showPassword ? 'text' : 'password'} fullWidth autoComplete="current-password" value={formData.password} onChange={handleChange} disabled={loading || loginStep === '2fa'} InputProps={{
                    startAdornment: (<InputAdornment position="start">
                        <Lock />
                      </InputAdornment>),
                    endAdornment: loginStep === 'credentials' ? (<InputAdornment position="end">
                        <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" disabled={loading}>
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>) : undefined,
                }} sx={{
                    mb: 1,
                    display: loginStep === 'credentials' ? 'block' : 'none', // Hide when in 2FA step
                    '& .MuiOutlinedInput-root': {
                        '& input[type="password"]::-webkit-credentials-auto-fill-button': {
                            display: 'none !important',
                        },
                        '& input::-ms-reveal': {
                            display: 'none !important',
                        },
                        '& input::-ms-clear': {
                            display: 'none !important',
                        },
                    },
                }}/>

                {/* Forgot password link */}
                <Box sx={{
                    textAlign: 'right',
                    mb: 2,
                    display: loginStep === 'credentials' ? 'block' : 'none',
                }}>
                  <MuiLink href={forgotPasswordUrl} variant="body2" sx={{ textDecoration: 'none' }}>
                    Forgot password?
                  </MuiLink>
                </Box>

                {/* 2FA field - always in DOM for password manager detection */}
                <TextField id="modal-2fa-code" name="totp" label="Verification Code" type="text" fullWidth autoComplete="one-time-code" placeholder="123456" value={formData.verificationCode} onChange={handleChange} disabled={loading} required={loginStep === '2fa'} helperText={loginStep === '2fa' ? "Enter the 6-digit code from your authenticator app" : undefined} inputRef={totpInputRef} inputProps={{
                    id: 'modal-2fa-code',
                    name: 'totp',
                    inputMode: 'numeric',
                    pattern: '[0-9]*',
                    maxLength: 6,
                    autoComplete: 'one-time-code',
                }} sx={{
                    mb: 2,
                    display: loginStep === '2fa' ? 'block' : 'none', // Hide visually but keep in DOM
                }}/>

                <Button type="submit" variant="contained" fullWidth size="large" disabled={loading} sx={{ mb: 2, py: 1.5, fontWeight: 600 }}>
                  {loading ? <CircularProgress size={24}/> : loginStep === '2fa' ? 'Verify & Login' : 'Login'}
                </Button>

                {import.meta.env.VITE_META_LOGIN_ENABLED === 'true' && loginStep !== '2fa' ? (
                  <Button
                    href="/api/auth/facebook/start"
                    variant="outlined"
                    fullWidth
                    size="large"
                    sx={{ mb: 2, py: 1.5, fontWeight: 600, textTransform: 'none' }}
                  >
                    Continue with Facebook
                  </Button>
                ) : null}

                {loginStep === '2fa' && (<Button onClick={() => {
                        setLoginStep('credentials');
                        setFormData(prev => ({ ...prev, verificationCode: '' }));
                        setError('');
                    }} fullWidth disabled={loading}>
                    Back to Login
                  </Button>)}
              </Box>)}

            {/* Passkey login */}
            {loginMethod === 'passkey' && (
              <Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={28} /></Box>}>
                <PasskeyLogin onSuccess={handlePasskeySuccess} onFallback={handlePasskeyFallback} email={formData.email} />
              </Suspense>
            )}

            {/* Signup link */}
            <Divider sx={{ my: 3 }}/>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Don't have an account?{' '}
                <MuiLink href={signupUrl} sx={{ textDecoration: 'none', fontWeight: 600 }}>
                  Create one now
                </MuiLink>
              </Typography>
            </Box>
          </Box>)}
      </DialogContent>
    </Dialog>);
};
export default LoginModal;
